import React, { useEffect, useState } from 'react';
import { getWarmupArticles, updateWarmupArticleVisibility, deleteWarmupArticle } from '../../services/WarmupArticleServiceFirestore';
import { WarmupArticle } from '../../models/WarmupArticle';
import ReactMarkdown from 'react-markdown';
import Layout from '../../components/Layout';
import styled from 'styled-components';
import { Card, SectionTitle, Button } from '../../components/StyledTableComponents';
import { theme } from '../../theme';
import { FaBook, FaEye, FaEyeSlash, FaTrash, FaClock, FaUser, FaArrowLeft, FaTag, FaNewspaper } from 'react-icons/fa';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';
import { useAuth } from '../../context/AuthContext';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

// Contenedor principal sin fondo
const BlogContainer = styled.div`
  min-height: 100vh;
  background: transparent;
  padding: 20px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
  }
`;

const BlogWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: transparent;
  min-height: 100vh;
  
  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100%;
  }
`;

const ContentArea = styled.div`
  width: 100%;
`;

// Lista de artículos en una sola columna
const ArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 0;
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 20px;
  }
`;

// Card de artículo simple sin efectos complejos
const SimpleArticleCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  cursor: pointer;
  position: relative;
  border-left: 4px solid ${theme.colors.primary.main};
  
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ArticleImageWrapper = styled.div`
  height: 300px;
  overflow: hidden;
  position: relative;
  background: #f5f5f5;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    height: 200px;
  }
`;

const ArticleContent = styled.div`
  padding: 30px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px;
  }
`;

const ArticleCategory = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.secondary.main};
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SimpleTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 12px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const ArticleExcerpt = styled.p`
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: #95a5a6;
  font-size: 0.9rem;
  font-weight: 500;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Vista detallada del artículo
const ArticleDetail = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-top: 0;
`;

const DetailHeader = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* Ratio 16:9 */
  overflow: hidden;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding-bottom: 56.25%; /* Mantenemos 16:9 en móvil también */
  }
`;

const DetailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DetailContent = styled.div`
  padding: 40px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px;
  }
  
  // Estilos para el contenido markdown
  h1, h2, h3, h4, h5, h6 {
    color: #2c3e50;
    font-weight: 700;
    margin: 2em 0 1em 0;
    line-height: 1.3;
  }
  
  h1 { font-size: 2.2rem; }
  h2 { font-size: 1.8rem; }
  h3 { font-size: 1.5rem; }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #444;
    margin: 0 0 1.5em 0;
    text-align: justify;
  }
  
  blockquote {
    border-left: 4px solid ${theme.colors.primary.main};
    padding-left: 20px;
    margin: 2em 0;
    font-style: italic;
    color: #666;
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
  }
  
  code {
    background: #f8f9fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 0.9em;
  }
  
  ul, ol {
    margin: 1.5em 0;
    padding-left: 2em;
  }
  
  li {
    margin: 0.5em 0;
    line-height: 1.6;
  }
`;

const DetailTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  margin: 0 0 16px 0;
  letter-spacing: -0.02em;
  color: #2c3e50;
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 30px;
  
  &:hover {
    background: #0056b3;
  }
`;

// Controles de admin simples
const AdminSection = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const SimpleAdminButton = styled.button<{ $type: 'visibility' | 'delete'; $visible?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  transition: opacity 0.3s ease;
  
  ${props => props.$type === 'visibility' && `
    background: ${props.$visible ? theme.colors.success.main : theme.colors.error.main};
    color: white;
    
    &:hover {
      opacity: 0.8;
    }
  `}
  
  ${props => props.$type === 'delete' && `
    background: ${theme.colors.error.main};
    color: white;
    
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const AdminBadge = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: ${props => props.$visible ? theme.colors.success.main : theme.colors.error.main};
  z-index: 10;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  
  svg {
    font-size: 3rem;
    margin-bottom: 20px;
    opacity: 0.3;
  }
  
  h3 {
    font-size: 1.4rem;
    margin-bottom: 10px;
    color: #495057;
  }
  
  p {
    font-size: 1rem;
    max-width: 400px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const WarmUpPage: React.FC = () => {
  const [articles, setArticles] = useState<WarmupArticle[]>([]);
  const [selected, setSelected] = useState<WarmupArticle|null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const { hasAnyRole } = useAuth();
  
  const isAdmin = hasAnyRole(['admin', 'superadmin']);

  const loadArticles = async () => {
    try {
      const res = await getWarmupArticles();
      setArticles(res);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const handleToggleVisibility = async (articleId: string, currentVisibility: boolean) => {
    if (!isAdmin || !articleId) return;
    
    setUpdatingId(articleId);
    try {
      await updateWarmupArticleVisibility(articleId, !currentVisibility);
      setArticles(prev => prev.map(article => 
        article.id === articleId 
          ? { ...article, visible: !currentVisibility }
          : article
      ));
    } catch (error) {
      console.error('Error updating article visibility:', error);
      alert('Error al actualizar la visibilidad del artículo');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteArticle = async (articleId: string, articleTitle: string) => {
    if (!isAdmin || !articleId) return;
    
    if (!window.confirm(`¿Estás seguro de que quieres eliminar el artículo "${articleTitle}"?`)) {
      return;
    }
    
    setUpdatingId(articleId);
    try {
      await deleteWarmupArticle(articleId);
      setArticles(prev => prev.filter(article => article.id !== articleId));
      if (selected?.id === articleId) {
        setSelected(null);
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Error al eliminar el artículo');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <BlogContainer>
          <BlogWrapper>
            <SectionHeaderComp icon={<FaNewspaper style={{ color: '#e4312e' }} />}>
              VRS WarmUp - Cargando...
            </SectionHeaderComp>
          </BlogWrapper>
        </BlogContainer>
      </Layout>
    );
  }

  const visibleArticles = isAdmin 
    ? articles 
    : articles.filter(article => article.visible !== false);

  return (
    <Layout>
      <BlogContainer>
        <BlogWrapper>
          {!selected ? (
            <>
              <SectionHeaderComp icon={<FaNewspaper style={{ color: '#e4312e' }} />}>
                VRS WarmUp
              </SectionHeaderComp>
              
              <ContentArea>
                {visibleArticles.length === 0 ? (
                  <EmptyState>
                    <FaBook />
                    <h3>No hay artículos disponibles</h3>
                    <p>
                      {isAdmin 
                        ? 'Aún no se han creado artículos. ¡Comienza escribiendo el primer artículo!' 
                        : 'Pronto habrá contenido increíble aquí. ¡Mantente atento!'
                      }
                    </p>
                  </EmptyState>
                ) : (
                  <ArticlesList>
                    {visibleArticles.map(article => (
                      <SimpleArticleCard 
                        key={article.id} 
                        onClick={() => setSelected(article)}
                      >
                        {isAdmin && (
                          <>
                            <AdminBadge $visible={article.visible !== false}>
                              {article.visible !== false ? (
                                <>
                                  <FaEye /> Visible
                                </>
                              ) : (
                                <>
                                  <FaEyeSlash /> Oculto
                                </>
                              )}
                            </AdminBadge>
                            
                            <AdminSection>
                              <SimpleAdminButton
                                $type="visibility"
                                $visible={article.visible !== false}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleVisibility(article.id!, article.visible !== false);
                                }}
                                disabled={updatingId === article.id}
                                title={article.visible !== false ? 'Ocultar artículo' : 'Mostrar artículo'}
                              >
                                {article.visible !== false ? <FaEye /> : <FaEyeSlash />}
                              </SimpleAdminButton>
                              
                              <SimpleAdminButton
                                $type="delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteArticle(
                                    article.id!, 
                                    article.titulo || `${article.circuito} (${article.ligaNombre})`
                                  );
                                }}
                                disabled={updatingId === article.id}
                                title="Eliminar artículo"
                              >
                                <FaTrash />
                              </SimpleAdminButton>
                            </AdminSection>
                          </>
                        )}
                        
                        {article.coverImageUrl && (
                          <ArticleImageWrapper>
                            <img 
                              src={article.coverImageUrl} 
                              alt="Portada del artículo" 
                            />
                          </ArticleImageWrapper>
                        )}
                        
                        <ArticleContent>
                          <ArticleCategory>
                            <FaTag />
                            {article.ligaNombre || 'VRS'}
                          </ArticleCategory>
                          
                          <SimpleTitle>
                            {article.titulo || `${article.circuito} (${article.ligaNombre})`}
                          </SimpleTitle>
                          
                          <ArticleExcerpt>
                            {article.contenidoMarkdown.replace(/[#*`]/g, '').slice(0, 150)}...
                          </ArticleExcerpt>
                          
                          <ArticleMeta>
                            <MetaItem>
                              <FaClock />
                              {new Date(article.fecha).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </MetaItem>
                            <MetaItem>
                              <FaUser />
                              {article.autor}
                            </MetaItem>
                          </ArticleMeta>
                        </ArticleContent>
                      </SimpleArticleCard>
                    ))}
                  </ArticlesList>
                )}
              </ContentArea>
            </>
          ) : (
            <>
              <SectionHeaderComp icon={<FaNewspaper style={{ color: '#e4312e' }} />}>
                {selected.titulo || `${selected.circuito} (${selected.ligaNombre})`}
              </SectionHeaderComp>
              
              <ArticleDetail>
                <BackButton onClick={() => setSelected(null)}>
                  <FaArrowLeft />
                  Volver al blog
                </BackButton>
                
                {selected.coverImageUrl && (
                  <DetailHeader>
                    <DetailImage 
                      src={selected.coverImageUrl} 
                      alt="Portada del artículo" 
                    />
                  </DetailHeader>
                )}
                
                <DetailContent>
                  <DetailTitle>
                    {selected.titulo || `${selected.circuito} (${selected.ligaNombre})`}
                  </DetailTitle>
                  
                  <ArticleMeta style={{ marginBottom: '40px' }}>
                    <MetaItem>
                      <FaClock />
                      {new Date(selected.fecha).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </MetaItem>
                    <MetaItem>
                      <FaUser />
                      {selected.autor}
                    </MetaItem>
                  </ArticleMeta>
                  
                  <ReactMarkdown>
                    {selected.contenidoMarkdown}
                  </ReactMarkdown>
                </DetailContent>
              </ArticleDetail>
            </>
          )}
        </BlogWrapper>
      </BlogContainer>
    </Layout>
  );
};

export default WarmUpPage;
